// @flow
import { useQuery, useMutation } from 'react-query';
import { toastr } from 'react-redux-toastr';
import ResidentService from '../../../services/residentService';

/**
 * Fetches start-transfer eligibility for a household.
 * Returns { enabled: boolean, disabledReasons: string[] }
 */
export function useStartTransferEligibility(
  organizationId: string,
  propertyId: string,
  householdId: string,
) {
  return useQuery(
    ['startTransferEligibility', organizationId, propertyId, householdId],
    () => {
      const service = new ResidentService();
      return service.getStartTransferEligibility(
        organizationId,
        propertyId,
        householdId,
      );
    },
    {
      enabled: Boolean(organizationId && propertyId && householdId),
      staleTime: 30_000,
      // Always refetch when the Snapshot remounts (e.g. returning from the
      // Complete-Renewal route) so eligibility can't be served stale.
      refetchOnMount: 'always',
    },
  );
}

/**
 * Returns a mutation function that creates a Draft UnitTransfer and navigates to Unit Availability on success.
 */
export function useCreateDraftUnitTransfer(
  organizationId: string,
  propertyId: string,
  householdId: string,
  onSuccess: () => void,
  errorMessage: string,
) {
  return useMutation(
    ({
      transferDate,
      transferReasonId,
    }: {
      transferDate: string,
      transferReasonId: string,
    }) => {
      const service = new ResidentService();
      return service.createDraftUnitTransfer(
        organizationId,
        propertyId,
        householdId,
        {
          transferDate,
          transferReasonId,
        },
      );
    },
    {
      onSuccess,
      onError: (error: Error) => {
        toastr.error(errorMessage, error?.message ?? '');
      },
    },
  );
}
